import { createContext, useContext, useState, useCallback } from 'react';

const GameContext = createContext(null);

const makeId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2)
  ).slice(0, 8);

const initialDraft = () => ({
  mode: 'ai', // 'ai' | 'custom'
  holes: 9,
  location: '',
  locationMode: 'search', // 'search' | 'near' | 'startBar'
  transport: 'walking', // 'walking' | 'driving'
  groupSize: 'medium', // 'small' | 'medium' | 'large'
  startTime: '8pm', // '6pm' | '7pm' | '8pm' | '9pm+'
  menuStyle: 'mixed', // 'mixed' | 'beer' | 'cocktails' | 'shots'
  budget: 'any', // 'cheap' | 'any' | 'nolimit' — matches generate.js
  difficulty: 'medium',
  teams: [{ id: makeId(), name: 'Team 1', players: [] }],
  // Custom-mode hand-built holes: { id, name, address, par, drink }
  customHoles: [],
  mysteryMode: false,
  caddyMode: false,
  cardRules: true,
});

// Shared lightweight draft state while user sets up a game.
export function GameProvider({ children }) {
  const [draft, setDraft] = useState(initialDraft);

  const [activeGame, setActiveGame] = useState(null);

  const updateDraft = useCallback((patch) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  }, []);

  const resetDraft = useCallback(() => {
    setDraft(initialDraft());
  }, []);

  return (
    <GameContext.Provider
      value={{ draft, updateDraft, resetDraft, activeGame, setActiveGame }}
    >
      {children}
    </GameContext.Provider>
  );
}

export function useGame() {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error('useGame must be used inside GameProvider');
  return ctx;
}
